import { apiRes, type AttachRedirectAttributionResponse, type PracticeAPI } from "@/practice/api";

const REDIRECT_CLICK_ID_PARAM = "redirect_click_id";
const ATTRIBUTION_SOURCE_PARAM = "attribution_source";
const REDIRECT_CLICK_ID_STORAGE_KEY = "redirect_attribution_click_id";
const PAID_SOCIAL_REDIRECT_CLICK_ID_STORAGE_KEY = "paid_social_redirect_attribution_click_id";
const ATTRIBUTION_SOURCE_STORAGE_KEY = "paid_social_attribution_source";
const REDIRECT_CLICK_ID_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
const ATTRIBUTION_SOURCE_PATTERN = /^[A-Za-z0-9_-]{1,64}$/;
const META_AD_SOURCES = new Set(["facebook", "fb", "ig", "instagram", "meta"]);

function normalizeRedirectClickId(value: string | null): string | null {
  if (value == null) {
    return null;
  }
  const trimmedValue = value.trim();
  if (!REDIRECT_CLICK_ID_PATTERN.test(trimmedValue)) {
    return null;
  }
  return trimmedValue;
}

function normalizeAttributionSource(value: string | null): string | null {
  if (value == null) {
    return null;
  }
  const trimmedValue = value.trim().toLowerCase();
  if (!ATTRIBUTION_SOURCE_PATTERN.test(trimmedValue)) {
    return null;
  }
  return trimmedValue;
}

function inferAttributionSource(urlParams: URLSearchParams): string | null {
  const explicitSource = normalizeAttributionSource(
    urlParams.get(ATTRIBUTION_SOURCE_PARAM) ?? urlParams.get("network"),
  );
  if (explicitSource !== null) {
    return explicitSource;
  }
  if (urlParams.has("fbclid")) {
    return "meta";
  }
  if (urlParams.has("ttclid")) {
    return "tiktok";
  }
  return null;
}

export function captureRedirectAttributionFromUrl(): void {
  if (typeof window === "undefined") {
    return;
  }
  const urlParams = new URLSearchParams(window.location.search);
  const redirectClickId = normalizeRedirectClickId(
    urlParams.get(REDIRECT_CLICK_ID_PARAM),
  );
  const attributionSource = inferAttributionSource(urlParams);
  if (redirectClickId == null) {
    if (attributionSource !== null) {
      try {
        window.localStorage.setItem(ATTRIBUTION_SOURCE_STORAGE_KEY, attributionSource);
      } catch (error) {
        console.error("Failed to persist redirect attribution source:", error);
      }
    }
    return;
  }
  try {
    window.localStorage.setItem(REDIRECT_CLICK_ID_STORAGE_KEY, redirectClickId);
    window.localStorage.setItem(PAID_SOCIAL_REDIRECT_CLICK_ID_STORAGE_KEY, redirectClickId);
    if (attributionSource === null) {
      window.localStorage.removeItem(ATTRIBUTION_SOURCE_STORAGE_KEY);
    } else {
      window.localStorage.setItem(ATTRIBUTION_SOURCE_STORAGE_KEY, attributionSource);
    }
  } catch (error) {
    console.error("Failed to persist redirect attribution:", error);
  }
}

export function hasMetaAdAttribution(): boolean {
  if (typeof window === "undefined") {
    return false;
  }
  try {
    const urlSource = inferAttributionSource(new URLSearchParams(window.location.search));
    const source = urlSource ?? normalizeAttributionSource(
      window.localStorage.getItem(ATTRIBUTION_SOURCE_STORAGE_KEY),
    );
    return source !== null && META_AD_SOURCES.has(source);
  } catch (error) {
    console.error("Failed to read redirect attribution source:", error);
    return false;
  }
}

export function getStoredRedirectClickId(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const primaryRedirectClickId = getPendingRedirectClickId();
    if (primaryRedirectClickId !== null) {
      return primaryRedirectClickId;
    }
    return normalizeRedirectClickId(
      window.localStorage.getItem(PAID_SOCIAL_REDIRECT_CLICK_ID_STORAGE_KEY),
    );
  } catch (error) {
    console.error("Failed to read redirect attribution:", error);
    return null;
  }
}

export function hasPendingRedirectAttribution(): boolean {
  return getPendingRedirectClickId() !== null;
}

export function getPendingRedirectClickId(): string | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    return normalizeRedirectClickId(
      window.localStorage.getItem(REDIRECT_CLICK_ID_STORAGE_KEY),
    );
  } catch (error) {
    console.error("Failed to read pending redirect attribution:", error);
    return null;
  }
}

export function clearStoredRedirectClickId(): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.removeItem(REDIRECT_CLICK_ID_STORAGE_KEY);
  } catch (error) {
    console.error("Failed to clear redirect attribution:", error);
  }
}

export async function attachStoredRedirectAttribution(
  API: PracticeAPI,
): Promise<void> {
  const redirectClickId = getPendingRedirectClickId();
  if (redirectClickId == null) {
    return;
  }
  const attachResult = apiRes<AttachRedirectAttributionResponse>();
  await API.attachRedirectAttribution(redirectClickId, attachResult);
  if (
    attachResult.error.value == null &&
    attachResult.data.value != null &&
    attachResult.data.value.success === true
  ) {
    clearStoredRedirectClickId();
    return;
  }
  console.error(
    "Failed to attach redirect attribution:",
    attachResult.error.value,
    attachResult.data.value,
  );
}
